import { ref } from 'vue';
import { storefrontRequest } from '@/lib/storefront-api';
import { company } from '@/data/company';

import feaOne from '@/assets/img/svg/fea-1.svg';
import feaTwo from '@/assets/img/svg/fea-2.svg';
import feaThree from '@/assets/img/svg/fea-3.svg';
import feaFour from '@/assets/img/svg/fea-4.svg';
import feaFive from '@/assets/img/svg/fea-5.svg';
import sofa from '@/assets/img/svg/sofa.svg';
import bed from '@/assets/img/svg/bed.svg';
import bedTwo from '@/assets/img/svg/bed-2.svg';
import chair from '@/assets/img/svg/chair.svg';
import like from '@/assets/img/svg/like.svg';
import hand from '@/assets/img/svg/hand.svg';
import letter from '@/assets/img/svg/letter.svg';
import about from '@/assets/img/svg/about.svg';
import comment from '@/assets/img/svg/comment.svg';

/**
 * The admin stores an icon by name; the bundler needs a real import to emit the
 * file. An explicit map keeps both true without a dynamic require, which webpack
 * can only resolve by bundling the whole directory.
 *
 * Keys must match SiteContent::ICONS on the API side.
 */
const ICONS = {
    'fea-1': feaOne,
    'fea-2': feaTwo,
    'fea-3': feaThree,
    'fea-4': feaFour,
    'fea-5': feaFive,
    sofa,
    bed,
    'bed-2': bedTwo,
    chair,
    like,
    hand,
    letter,
    about,
    comment,
};

export function iconUrl(name) {
    return ICONS[name] || feaOne;
}

/**
 * What the pages render before the request answers, and what they keep rendering
 * if it never does. Mirrors SiteContent::defaults() on the API side.
 *
 * This is not belt-and-braces: the hero is the first paint of the homepage, so
 * without a bundled fallback an API hiccup would show an empty banner rather than
 * slightly stale copy.
 */
const FALLBACK = {
    hero: {
        eyebrow: 'All products in Store',
        title: 'Get 25% Off',
        title_highlight: '25%',
        description: 'Unlock unbeatable savings on every purchase. Introducing our new collection of stylish and comfortable sofas, designed to elevate your living space with modern elegance.',
        button_label: "Let's Shop Now",
        button_link: '/shop-v2',
    },
    about: {
        story_title: 'Our Story Journey',
        story_body: 'Sweet Home Gallery designs and builds furniture in our Ghazir workshop.',
        why_title: 'Why You Choose Us',
        why_intro: '',
        stats_title: 'By the Numbers',
        stats_intro: '',
        video_url: '',
    },
    features: [],
    stats: [],
    terms: { updated_at: '', sections: [] },
};

// Module-level so the homepage and the about page share one request rather than
// each fetching the same document on mount.
let pending = null;
const content = ref(FALLBACK);
const loaded = ref(false);

export function useSiteContent() {
    if (!pending) {
        pending = storefrontRequest('/site-content')
            .then((response) => {
                const data = response?.data;
                if (data) {
                    // Section-merged, not replaced: a payload from an older API that
                    // is missing a field still renders that field from FALLBACK.
                    content.value = {
                        hero: { ...FALLBACK.hero, ...(data.hero || {}) },
                        about: { ...FALLBACK.about, ...(data.about || {}) },
                        features: Array.isArray(data.features) ? data.features : FALLBACK.features,
                        stats: Array.isArray(data.stats) ? data.stats : FALLBACK.stats,
                        terms: data.terms && Array.isArray(data.terms.sections)
                            ? { updated_at: data.terms.updated_at || '', sections: data.terms.sections }
                            : FALLBACK.terms,
                    };
                }
                return content.value;
            })
            .catch(() => content.value) // keep the fallback; the page still renders
            .finally(() => { loaded.value = true; });
    }

    return { content, loaded, ready: pending };
}

/**
 * Splits a headline around the phrase the admin marked for the accent colour.
 * Case-insensitive, first occurrence only — the admin preview does exactly this,
 * so what they see there is what ships.
 */
export function splitHighlight(title = '', highlight = '') {
    const needle = (highlight || '').trim();

    if (!needle) {
        return [{ text: title, highlighted: false }];
    }

    const at = title.toLowerCase().indexOf(needle.toLowerCase());

    if (at === -1) {
        return [{ text: title, highlighted: false }];
    }

    return [
        { text: title.slice(0, at), highlighted: false },
        { text: title.slice(at, at + needle.length), highlighted: true },
        { text: title.slice(at + needle.length), highlighted: false },
    ].filter((part) => part.text !== '');
}

/**
 * Swaps {phone}, {address} and friends for the real company details.
 *
 * The admin types the placeholder rather than the number, so company.js stays the
 * single place a phone number changes and the terms follow it — instead of a
 * stale number sitting in a legal page nobody re-reads.
 */
const TOKENS = {
    '{name}': () => company.name,
    '{phone}': () => company.phone,
    '{address}': () => company.address,
    '{email}': () => company.email,
    '{hours}': () => company.hours,
};

export function fillTokens(text = '') {
    return Object.entries(TOKENS).reduce(
        // split/join rather than a regex: a company detail containing $& or $1
        // would otherwise be mangled by replace()'s substitution patterns.
        (out, [token, read]) => out.split(token).join(read() || ''),
        String(text),
    );
}

/** Blank-line-separated paragraphs, the way the admin textarea explains it. */
export function paragraphs(body = '') {
    return String(body)
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);
}
